import { useQueryClient, type Query, type QueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

import { clearOutgoingMessages } from '#modules/chat/components/Chats/utils/outgoingBoxes';
import { clearChatDrafts } from '#modules/chat/model/chatDraftStore';
import { clearPostFormDraft } from '#modules/finder/features/posts/model/postFormStore';
import { endSession } from '#modules/user/features/auth/model/logout';
import { logout as serverLogout } from '#modules/user/features/auth/utils/logout';
import { clearOAuthReturn } from '#modules/user/features/auth/utils/oauthReturnIntent';
import { getMeOptions } from '#modules/user/model/me';
import { beginNativeSignOut, clearNativeToken } from '#features/api/utils/nativeAuth';
import { stopNativeCallsForSignOut } from '#features/calls/utils/nativeCalls';
import { detachPushToken } from '#features/notifications/utils/detachPushToken';
import { beginNativePushSignOut } from '#features/notifications/utils/nativePushRegistration';
import { leaveVoice } from '#features/voice/model/voice';
import { localStorage } from '#utils/localStorage';

const clearAuthCookieOnClient = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const expiredAt = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const baseCookie = `authToken=; Expires=${expiredAt}; Max-Age=0; Path=/`;

  document.cookie = `${baseCookie}; SameSite=Lax`;
  document.cookie = `${baseCookie}; SameSite=None; Secure`;
};

/**
 * Что переживает выход: общий каталог, одинаковый для всех (игры, платформы, языки, страны,
 * интересы, настройки игр, блог, посадочные страницы). Посадочная, куда уводит выход, тут же его
 * читает, и сносить его значило бы мигнуть скелетами. Всё остальное - данные человека.
 */
const PUBLIC_QUERY_ROOTS = new Set([
  'games',
  'platforms',
  'languages',
  'countries',
  'interests',
  'gameSettings',
  'blogPosts',
  'blogPost',
  'landingPage',
]);

const isPersonalQuery = (query: Query) => !PUBLIC_QUERY_ROOTS.has(String(query.queryKey[0]));

// useAuth() lives in many components at once, and an expired session makes all of them reach
// for logout in the same tick. Share one run so that becomes a single request instead of one
// per consumer.
let logoutInFlight: Promise<void> | null = null;

/** Стереть снимки приложения. Сбой не держит выход: токен всё равно уйдёт следом. */
const discardNativeSnapshots = async (queryClient: QueryClient) => {
  try {
    const { iosNavigation } = await import('#features/layout/native/iosNavigation');
    await iosNavigation.resetForIdentity();
  } catch {
    // Logout still proceeds if the bridge chunk could not be loaded.
  }
  try {
    const { discardSnapshots } = await import('#features/snapshot/save');
    await discardSnapshots(queryClient, { pause: true });
  } catch {
    // best-effort
  }
};

export const useAuth = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  // Both callbacks invalidate the router, which re-renders everything below it. They must keep
  // a stable identity: callers put them in effect dependencies (see useProfile), and a fresh
  // function every render re-runs those effects — with `logout` that means invalidate →
  // re-render → logout again, a loop that pins the main thread and freezes the tab.
  const logout = useCallback(() => {
    if (logoutInFlight) {
      return logoutInFlight;
    }

    logoutInFlight = (async () => {
      // Приложение: с этой минуты ответы уходящего сеанса ничего не пишут на телефон (токен, id
      // аккаунта), даже если долетят после того, как выход их стёр.
      beginNativeSignOut();
      beginNativePushSignOut();
      // Stop real iOS capture and detach the separate VoIP token with the leaving JWT.
      await stopNativeCallsForSignOut().catch(() => undefined);

      // Выбрасываем профиль из кеша, а не помечаем устаревшим: invalidate оставляет данные
      // лежать, и следующий `useQuery(['me'])` в этой же вкладке сперва отдаёт профиль
      // ПРЕДЫДУЩЕГО пользователя. После «удалить аккаунт» это ломало вход: человек вводил
      // пароль живого аккаунта, попадал на /dashboard — и через полсекунды useProfile видел
      // в кеше `deleted: true` от удалённого профиля и разлогинивал его обратно на /sign-in.
      // Со стороны это выглядит как «вход не работает»: несколько попыток подряд отбивает,
      // потом случайно везёт с таймингом (refetch успевает раньше эффекта).
      queryClient.removeQueries({ queryKey: getMeOptions().queryKey });

      // Первым делом рвём голос: пока соединение с медиасервером живо, микрофон открыт и человека
      // слышно в комнате — уже вышедшим из аккаунта. Делать это надо до смерти куки, иначе
      // серверу нечем опознать, кто уходит.
      await leaveVoice(queryClient);

      // Before the cookie dies — the mutation needs the session to know whose token to release.
      await detachPushToken();

      // Сеанс в реестре устройств — тоже до смерти куки: без токена шлюз не узнает, какой именно
      // вход завершать. Лучшее усилие: выход не ждёт удачи и не зависит от неё.
      await endSession();

      try {
        await serverLogout();
      } catch (error) {
        console.error('Failed to logout', error);
      } finally {
        clearAuthCookieOnClient();
        // Приложение: сохранённые на телефоне лента, профиль и переписки уходят раньше токена -
        // и запись новых останавливается до следующего входа. Иначе ответ, долетевший уже после
        // выхода, положил бы на телефон данные ушедшего человека.
        if (import.meta.env.VITE_IS_NATIVE === 'true') {
          await discardNativeSnapshots(queryClient);
        }
        await clearNativeToken();
        localStorage.deleteItem('token');
        // Черновик заявки (текст, настройки), недописанные сообщения в переписках и
        // незабранная записка о возврате с привязки аккаунта не переживают выход: без этого
        // следующий, кто войдёт в этой вкладке, открыл бы форму заявки с чужим текстом, а чат –
        // с чужим недописанным сообщением. На общем компьютере это чужая переписка на экране.
        clearPostFormDraft();
        clearChatDrafts();
        clearOAuthReturn();
        await router.invalidate();
        /*
          Данные человека - все, и только после перехода: до него текущий экран с
          `useSuspenseQuery` перечитал бы свои данные уже без токена. Раньше здесь не чистилось
          ничего, кроме профиля, и следующий вошедший в пределах `gcTime` видел ленту и чаты
          прошлого.

          Не `clear()`: смонтированные экраны его не замечают и показывают старое до следующей
          отрисовки, а заодно он сносил общий каталог, который посадочная тут же читала заново.
          Поэтому брошенное - убрать, а то, что на экране, - сбросить: `reset` перерисует экран и
          перечитает его уже как гостя (у посадочной тот же ключ ленты, что у вошедшего).
        */
        queryClient.removeQueries({ type: 'inactive', predicate: isPersonalQuery });
        void queryClient.resetQueries({ predicate: isPersonalQuery });
        // Ящик исходящих живёт при клиенте запросов, а не в его кеше.
        clearOutgoingMessages(queryClient);
      }
    })().finally(() => {
      logoutInFlight = null;
    });

    return logoutInFlight;
  }, [queryClient, router]);

  // Awaiting the invalidation matters: it is what re-runs the root `beforeLoad`, so the router
  // context stops saying "anonymous" and the `_authed` guard lets the fresh session through.
  // Returning early (as this used to) left callers navigating with the stale context — the guard
  // bounced them to /sign-in, which bounced them on to /dashboard.
  const login = useCallback(
    async (options: { invalidate: boolean } = { invalidate: true }) => {
      queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
      if (options?.invalidate !== false) {
        await router.invalidate();
      }
    },
    [queryClient, router]
  );

  return { logout, login };
};
